import { useEffect, useRef, useState } from 'react';
import { api, logout, API_ORIGIN } from '../api.ts';
import * as native from '../native.ts';
import * as passkeys from '../passkeys.ts';
import * as push from '../push.ts';
import { readLockPreference, writeLockPreference } from '../AppLock.tsx';
import { useApp, useFetch, useMoney, usePrivateMoney } from '../store.tsx';
import { Card, CopyableSecret, Field, SectionTitle, Sheet, Spinner, Toggle } from '../components.tsx';
import { ConnectPhoneSheet } from './ConnectPhone.tsx';
import { IngestActivity } from './IngestActivity.tsx';
import { formatDate, parseAmountInput } from '../format.ts';
import { minorToDecimal } from '@fintrack/core';

interface Source {
  id: string; type: string; label: string | null; status: string;
  last_event_at: string | null; error_message: string | null; has_key: boolean;
  /** Raw events delivered through this source, for the delete warning. */
  event_count: number;
  /** Account this source's messages file into when they name no card. */
  default_account_id: string | null;
  /** Where this source's messages have actually ended up, busiest first. */
  routed_accounts: { id: string; displayName: string; last4: string | null; count: number }[];
}

/**
 * `pending_setup` is a database value, not a sentence. Shown raw it reads as a
 * fault rather than what it is: a key was issued and the device has not used it
 * yet.
 *
 * What has actually arrived decides the wording, ahead of the status column.
 * Rotating a key used to stamp a source 'active' whether or not it had ever
 * worked, so older rows claim to be live on no evidence — the event count is
 * the honest signal and never disagrees with itself.
 */
function sourceStatusLabel(s: { type: string; status: string; event_count: number }, locale: string): string {
  const ar = locale === 'ar';
  // Manual and CSV have no device and no key, so they are never "waiting to be
  // set up" — and they file transactions directly rather than through raw
  // events, so their count is always zero and says nothing.
  if (s.type === 'manual') return ar ? 'إدخال يدوي' : 'Entered by hand';
  if (s.type === 'csv') return ar ? 'استيراد ملف' : 'File import';
  if (s.status === 'paused') return ar ? 'موقوف' : 'Paused';
  if (s.status === 'error') return ar ? 'خطأ' : 'Not working';
  if (s.event_count === 0) return ar ? 'لم يُعدّ بعد' : 'Not set up yet';
  if (s.event_count === 1) return ar ? 'رسالة واحدة' : '1 message';
  return ar ? `${s.event_count} رسالة` : `${s.event_count} messages`;
}

interface Account {
  id: string; institution: string | null; displayName: string; accountType: string;
  currency: string; last4: string | null; balanceMinor: number | null; balanceAsOf: string | null;
}

const ALERT_LABELS: Record<string, { en: string; ar: string }> = {
  high_spending: { en: 'Unusually high spending', ar: 'إنفاق مرتفع بشكل غير معتاد' },
  budget_exceeded: { en: 'Budget exceeded', ar: 'تجاوز الميزانية' },
  budget_warning: { en: 'Approaching a budget limit', ar: 'الاقتراب من حد الميزانية' },
  duplicate_detected: { en: 'Possible duplicate transaction', ar: 'حركة مكررة محتملة' },
  recurring_detected: { en: 'New recurring payment found', ar: 'اكتشاف دفعة متكررة جديدة' },
  subscription_price_increase: { en: 'Subscription price increased', ar: 'ارتفاع سعر اشتراك' },
  unusual_merchant: { en: 'Unfamiliar merchant', ar: 'تاجر غير معتاد' },
  large_transaction: { en: 'Large transaction', ar: 'معاملة كبيرة' },
  spending_trend_increase: { en: 'Spending trending up', ar: 'اتجاه الإنفاق للارتفاع' },
};

export function SettingsScreen({ onSignedOut, onOpenImport }: {
  onSignedOut: () => void;
  onOpenImport: () => void;
}): JSX.Element {
  const { t, locale, setLocale, user, refreshUser, toast, activeAccountId, setActiveAccountId, toastError } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const [sheet, setSheet] = useState<null | 'password' | 'delete-data' | 'delete-account' | 'alerts' | 'sources' | 'iphone' | 'profile' | 'activity' | 'add-account'>(null);
  const [newKey, setNewKey] = useState<{ type: string; key: string } | null>(null);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [linkSource, setLinkSource] = useState<Source | null>(null);

  const [sendingStatement, setSendingStatement] = useState(false);

  const sources = useFetch<{ sources: Source[] }>('/sources');
  const prefs = useFetch<{ alerts: Record<string, boolean> }>('/preferences');

  const setStatementEmail = async (value: boolean) => {
    if (!prefs.data) return;
    try {
      await api.put('/preferences/alerts', { value: { ...prefs.data.alerts, statement_email: value } });
      prefs.reload();
    } catch (err) {
      toastError(err);
    }
  };

  /**
   * The same statement the schedule sends, on demand.
   *
   * Reports what actually happened. The endpoint answers `sent: false` when
   * the closed cycle held no transactions, and saying "done" to that would
   * leave someone waiting for a mail that is never coming.
   */
  const emailStatementNow = async () => {
    setSendingStatement(true);
    try {
      const res = await api.post<{ sent: boolean; to: string | null }>('/me/statement', {});
      toast(res.sent
        ? (locale === 'ar' ? `أُرسل إلى ${res.to}` : `Sent to ${res.to}`)
        : (locale === 'ar'
            ? 'لا توجد حركات في الدورة المنتهية، فلا يوجد كشف لإرساله.'
            : 'The last closed cycle had no transactions, so there is nothing to send.'));
    } catch (err) {
      toastError(err);
    } finally {
      setSendingStatement(false);
    }
  };
  const accounts = useFetch<{ accounts: Account[] }>('/accounts');

  const removeSource = async (s: Source) => {
    // A source that has delivered transactions is a working connection: deleting
    // it stops the phone that uses it, so that case gets a heavier warning than
    // tidying away one that was never set up. Past transactions survive either
    // way — raw events keep their history and simply lose the link.
    const message = s.event_count > 0
      ? (locale === 'ar'
          ? `${s.label ?? s.type}: أرسل ${s.event_count} رسالة. حذفه يوقف الجهاز الذي يستخدمه. تبقى حركاتك السابقة.`
          : `${s.label ?? s.type} has delivered ${s.event_count} messages. Deleting it stops whatever device uses it. Your existing transactions are kept.`)
      : (locale === 'ar'
          ? `حذف ${s.label ?? s.type}؟ لم يُستخدم قط.`
          : `Delete ${s.label ?? s.type}? It has never been used.`);
    if (!window.confirm(message)) return;

    try {
      await api.del(`/sources/${s.id}`);
      sources.reload();
      toast(t('done'));
    } catch (err) {
      toastError(err);
    }
  };

  const toggleAi = async (value: boolean) => {
    try {
      await api.patch('/me', { aiConsent: value });
      await refreshUser();
      toast(t('done'));
    } catch (err) {
      toastError(err);
    }
  };

  const exportData = async () => {
    try {
      const data = await api.get<unknown>('/me/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fintrack-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast(t('done'));
    } catch (err) {
      toastError(err);
    }
  };

  const signOut = async () => {
    await logout();
    onSignedOut();
  };

  /**
   * Hands a fresh key to the Shortcuts action, where there is one.
   *
   * In the native app the user never has to see this key at all — the "Add to
   * Fintrack" action reads it from the shared keychain. Copying it by hand is
   * the step that has gone wrong for everyone who has set this up, so on iOS
   * it simply stops being a step.
   */
  const shareKeyWithShortcut = (key: string) => {
    void native.shareIngestConfig(API_ORIGIN, key);
  };

  const addSource = async (type: string) => {
    try {
      const res = await api.post<{ id: string; type: string; ingestKey?: string }>('/sources', { type });
      if (res.ingestKey) { setNewKey({ type: res.type, key: res.ingestKey }); shareKeyWithShortcut(res.ingestKey); }
      sources.reload();
    } catch (err) {
      toastError(err);
    }
  };

  const rotateKey = async (id: string, type: string) => {
    try {
      const res = await api.post<{ ingestKey: string }>(`/sources/${id}/rotate-key`);
      setNewKey({ type, key: res.ingestKey });
      // A rotated key must replace the stored one, or the Shortcut keeps
      // posting with a credential the server has just stopped accepting.
      shareKeyWithShortcut(res.ingestKey);
      sources.reload();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="screen">
      <h1 style={{ fontSize: 21, fontWeight: 680, marginBottom: 4 }}>{t('settings')}</h1>

      <PrivateAmountsCard />

      <SectionTitle action={
        <button className="btn btn--ghost btn--sm" onClick={() => setSheet('profile')}>{t('edit')}</button>
      }>
        {t('profile')}
      </SectionTitle>
      <Card>
        {/* Email is the sign-in identity. Changing it needs a verification
            round-trip that does not exist yet, so it stays read-only and says
            why rather than offering a field that would half-work. */}
        <Row label={t('email')} value={user?.email ?? '—'} />
        <Row label={t('name')} value={user?.name || '—'} />
        <Row label={t('country')} value={user?.country ?? '—'} />
        <Row label={t('currency')} value={user?.currency ?? '—'} />
        {/* A dash here was the app refusing to say a number it already had.
            When nothing is stated, the last salary stands in — shown as what
            it is, so it reads as an observation rather than as something the
            user typed and forgot. */}
        <Row
          label={t('monthlyIncome')}
          value={user?.monthlyIncomeMinor != null
            ? privateMoney(user.monthlyIncomeMinor, user.currency)
            : user?.detectedIncome
              ? privateMoney(user.detectedIncome.amountMinor, user.currency)
              : '—'}
          hint={user?.monthlyIncomeMinor == null && user?.detectedIncome
            ? (locale === 'ar'
                ? `من راتبك في ${formatDate(user.detectedIncome.occurredAt, locale)}`
                : `from your salary on ${formatDate(user.detectedIncome.occurredAt, locale)}`)
            : undefined}
        />
      </Card>

      <PayCycleCard />

      <SectionTitle>{t('language')}</SectionTitle>
      <Card>
        <div className="row" style={{ gap: 8 }}>
          <button className={`chip${locale === 'en' ? ' chip--active' : ''}`} onClick={() => setLocale('en')}>English</button>
          <button className={`chip${locale === 'ar' ? ' chip--active' : ''}`} onClick={() => setLocale('ar')}>العربية</button>
        </div>
      </Card>

      <SectionTitle action={
        <button className="btn btn--ghost btn--sm" onClick={() => setSheet('add-account')}>
          {locale === 'ar' ? '+ حساب' : '+ Add'}
        </button>
      }>
        {t('accounts')}
      </SectionTitle>

      {/* Which account the rest of the app is showing. "All accounts" is the
          default and stays first, so the way back is never buried. */}
      {(accounts.data?.accounts.length ?? 0) > 1 && (
        <Card style={{ marginBottom: 10 }}>
          <div className="tiny muted" style={{ marginBottom: 8 }}>
            {locale === 'ar' ? 'الحساب المعروض في التطبيق' : 'Showing in the app'}
          </div>
          <div className="scroll-x" role="tablist">
            <button
              role="tab"
              aria-selected={!activeAccountId}
              className={`chip${!activeAccountId ? ' chip--active' : ''}`}
              onClick={() => setActiveAccountId(null)}
            >
              {locale === 'ar' ? 'كل الحسابات' : 'All accounts'}
            </button>
            {accounts.data?.accounts.map((a) => (
              <button
                key={a.id}
                role="tab"
                aria-selected={activeAccountId === a.id}
                className={`chip${activeAccountId === a.id ? ' chip--active' : ''}`}
                onClick={() => setActiveAccountId(a.id)}
              >
                {a.displayName}
              </button>
            ))}
          </div>
        </Card>
      )}
      <Card>
        {accounts.data?.accounts.length ? accounts.data.accounts.map((a) => (
          // Accounts are auto-created from bank messages with a generated name
          // like "Card •1871", so renaming them is the first thing anyone wants.
          <button
            key={a.id}
            className="row row--between"
            style={{ paddingBlock: 9, background: 'none', width: '100%', textAlign: 'start' }}
            onClick={() => setEditAccount(a)}
          >
            <span className="grow">
              <span className="small strong" style={{ display: 'block' }}>{a.displayName}</span>
              <span className="tiny muted">{a.institution ?? a.accountType}</span>
            </span>
            {a.balanceMinor != null && (
              <span className="small money">{money(a.balanceMinor, a.currency)}</span>
            )}
            <span className="tiny muted" aria-hidden style={{ marginInlineStart: 8 }}>›</span>
          </button>
        )) : <div className="small muted center" style={{ padding: 10 }}>{t('none')}</div>}
      </Card>

      <SectionTitle action={
        <button className="btn btn--ghost btn--sm" onClick={() => setSheet('sources')}>+ {t('addSource')}</button>
      }>
        {t('transactionSources')}
      </SectionTitle>
      <Card>
        {sources.data?.sources.map((s) => (
          <div key={s.id} className="row row--between" style={{ paddingBlock: 9, gap: 10 }}>
            <span className="grow">
              <span className="small strong" style={{ display: 'block' }}>{s.label ?? s.type}</span>
              <span className="tiny muted">
                {sourceStatusLabel(s, locale)}
                {s.last_event_at && ` · ${new Date(s.last_event_at).toLocaleDateString()}`}
              </span>
              {/* What this source actually does, not just its fallback setting.
                  Naming only the fallback read as "this key belongs to that
                  card", which is what makes three cards look like they need
                  three keys. */}
              {s.has_key && (
                <button
                  className="tiny"
                  style={{ color: 'var(--accent-text)', background: 'none', padding: '3px 0 0', textAlign: 'start' }}
                  onClick={() => setLinkSource(s)}
                >
                  {(() => {
                    const routed = s.routed_accounts ?? [];
                    if (routed.length > 1) {
                      const names = routed.map((a) => (a.last4 ? `•${a.last4}` : a.displayName)).join(', ');
                      return locale === 'ar'
                        ? `يوزّع حسب رقم البطاقة: ${names}`
                        : `Sorted by card number: ${names}`;
                    }
                    const linked = accounts.data?.accounts.find((a) => a.id === s.default_account_id);
                    if (routed.length === 1) {
                      const only = routed[0]!;
                      const name = only.last4 ? `•${only.last4}` : only.displayName;
                      return locale === 'ar' ? `يذهب إلى ${name}` : `Goes to ${name}`;
                    }
                    if (linked) {
                      return locale === 'ar'
                        ? `بلا رقم بطاقة → ${linked.displayName}`
                        : `No card number → ${linked.displayName}`;
                    }
                    return locale === 'ar' ? '+ اربطه بحساب' : '+ File into an account';
                  })()}
                </button>
              )}
            </span>
            {s.has_key && (
              <button className="btn btn--secondary btn--sm" onClick={() => rotateKey(s.id, s.type)}>
                {t('rotateKey')}
              </button>
            )}
            {/* Manual entry is where hand-added transactions are filed, so it
                has no key and nothing to disconnect — deleting it would only
                orphan its rows. */}
            {s.type !== 'manual' && (
              <button
                className="icon-btn icon-btn--danger"
                aria-label={`${t('delete')} ${s.label ?? s.type}`}
                onClick={() => removeSource(s)}
              >
                ✕
              </button>
            )}
          </div>
        ))}
        {/* iPhone users arrive expecting Android-style automatic capture. Put
            the real route in front of them rather than leaving them to
            discover that SMS reading does not exist on iOS. */}
        {/* Named after the phone the user actually set up, rather than
            offering iPhone instructions to an Android user. */}
        <button className="btn btn--secondary btn--block btn--sm" style={{ marginTop: 8 }}
                onClick={() => setSheet('iphone')}>
          {user?.devicePlatform === 'android'
            ? (locale === 'ar' ? '🤖 الإعداد على أندرويد' : '🤖 Set up on Android')
            : (locale === 'ar' ? '📱 الإعداد على الآيفون' : '📱 Set up on iPhone')}
        </button>
        <button className="btn btn--secondary btn--block btn--sm" style={{ marginTop: 8 }} onClick={onOpenImport}>
          {t('importCsv')}
        </button>
        {/* Findable without walking back through the setup flow — this is the
            screen someone opens when transactions are not appearing. */}
        <button className="btn btn--ghost btn--block btn--sm" style={{ marginTop: 4 }}
                onClick={() => setSheet('activity')}>
          {locale === 'ar' ? 'سجل الرسائل الواردة' : 'Incoming message log'}
        </button>
      </Card>

      <SectionTitle>{t('privacy')}</SectionTitle>
      <Card>
        <Toggle
          checked={user?.aiConsent ?? false}
          onChange={toggleAi}
          label={t('aiConsent')}
          hint={t('aiConsentHelp')}
        />
        <div className="divider" />
        <button className="btn btn--ghost btn--block" style={{ justifyContent: 'flex-start' }} onClick={() => setSheet('alerts')}>
          {t('alertSettings')}
        </button>
        <div className="divider" />
        <button className="btn btn--ghost btn--block" style={{ justifyContent: 'flex-start' }} onClick={exportData}>
          {t('exportData')}
        </button>
        <div className="tiny muted" style={{ paddingInline: 10, paddingBottom: 8 }}>{t('exportHelp')}</div>

        {/* Beside Export, not inside Alert settings.
            It was filed with the alerts on the reasoning that "what does
            Fintrack send me" is one question — but a person looking for a
            recurring export of their own data does not go looking under
            alerts, and the first person to try could not find it. It is an
            export that arrives on its own, so it lives with the exports. */}
        <div className="divider" />
        <Toggle
          checked={prefs.data?.alerts.statement_email !== false}
          onChange={setStatementEmail}
          label={locale === 'ar' ? 'كشف الدورة بالبريد' : 'Email me a statement each cycle'}
          hint={locale === 'ar'
            ? 'عند انتهاء كل دورة راتب، يصلك ملف جداول بكل حركاتها على بريدك المُوثَّق — نسخة تحتفظ بها بنفسك.'
            : 'When a pay cycle closes, a spreadsheet of its transactions is emailed to your verified address — a copy you keep yourself.'}
        />
        <button className="btn btn--ghost btn--block" style={{ justifyContent: 'flex-start' }}
                disabled={sendingStatement} onClick={emailStatementNow}>
          {sendingStatement
            ? '…'
            : (locale === 'ar' ? 'أرسل لي كشفاً الآن' : 'Email me one now')}
        </button>
      </Card>

      <VerifyEmailCard />

      <SectionTitle>{t('notifications')}</SectionTitle>
      <PushCard />

      <SectionTitle>{t('security')}</SectionTitle>
      <Card>
        <PasskeySection />
        <div className="divider" />
        <AppLockToggle />
        <div className="divider" />
        <button className="btn btn--ghost btn--block" style={{ justifyContent: 'flex-start' }} onClick={() => setSheet('password')}>
          {t('changePassword')}
        </button>
        <div className="divider" />
        <button className="btn btn--ghost btn--block" style={{ justifyContent: 'flex-start' }}
                onClick={async () => { await api.post('/auth/logout-all'); onSignedOut(); }}>
          {t('signOutAll')}
        </button>
      </Card>

      <div className="stack" style={{ marginTop: 22 }}>
        <button className="btn btn--secondary btn--block" onClick={signOut}>{t('signOut')}</button>
        <button className="btn btn--danger btn--block" onClick={() => setSheet('delete-data')}>{t('deleteData')}</button>
        <button className="btn btn--danger btn--block" onClick={() => setSheet('delete-account')}>{t('deleteAccount')}</button>
      </div>

      {/* App Store Connect requires a reachable privacy policy and support URL,
          and a reviewer looks for both inside the app too. Terms joined them
          when registration opened: a stranger signing up has agreed to
          something, and it should be possible to read what. */}
      <div className="row center" style={{ gap: 18, marginTop: 24, flexWrap: 'wrap' }}>
        <a className="tiny muted" href="/privacy.html" target="_blank" rel="noopener">
          {locale === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
        </a>
        <a className="tiny muted" href="/terms.html" target="_blank" rel="noopener">
          {locale === 'ar' ? 'شروط الخدمة' : 'Terms of Service'}
        </a>
        <a className="tiny muted" href="/support.html" target="_blank" rel="noopener">
          {locale === 'ar' ? 'الدعم' : 'Support'}
        </a>
      </div>

      <p className="tiny muted center" style={{ marginTop: 14, lineHeight: 1.65 }}>
        {locale === 'ar'
          ? 'لا تُباع بياناتك المالية ولا تُستخدم للإعلانات. تُحذف نصوص الرسائل الأصلية تلقائياً بعد فترة الاحتفاظ.'
          : 'Your financial data is never sold or used for advertising. Raw message text is deleted automatically after the retention period.'}
      </p>

      {sheet === 'iphone' && (
        <ConnectPhoneSheet
          platform={user?.devicePlatform ?? 'ios'}
          onClose={() => { setSheet(null); sources.reload(); }}
        />
      )}
      {sheet === 'activity' && (
        <Sheet title={locale === 'ar' ? 'سجل الرسائل الواردة' : 'Incoming message log'}
               onClose={() => setSheet(null)}>
          <div className="stack">
            <p className="tiny muted" style={{ lineHeight: 1.6 }}>
              {locale === 'ar'
                ? 'كل رسالة وصلت إلى هذا الحساب وما حدث لها. إن كانت القائمة فارغة فالمشكلة في الاختصار وليس في القراءة.'
                : 'Every message that reached this account, and what happened to it. An empty list means the problem is the Shortcut, not the reading.'}
            </p>
            <IngestActivity />
          </div>
        </Sheet>
      )}
      {sheet === 'add-account' && (
        <AddAccountSheet
          onClose={() => setSheet(null)}
          onSaved={() => { accounts.reload(); setSheet(null); }}
        />
      )}
      {linkSource && (
        <LinkSourceSheet
          source={linkSource}
          accounts={accounts.data?.accounts ?? []}
          onClose={() => setLinkSource(null)}
          onSaved={() => { sources.reload(); setLinkSource(null); }}
        />
      )}
      {sheet === 'password' && <ChangePasswordSheet onClose={() => setSheet(null)} />}
      {sheet === 'profile' && <EditProfileSheet onClose={() => setSheet(null)} />}
      {editAccount && (
        <EditAccountSheet
          account={editAccount}
          onClose={() => setEditAccount(null)}
          onSaved={() => accounts.reload()}
        />
      )}
      {sheet === 'alerts' && <AlertSettingsSheet onClose={() => setSheet(null)} />}
      {sheet === 'sources' && (
        <Sheet title={t('addSource')} onClose={() => setSheet(null)}>
          <div className="stack">
            {['sms', 'notification', 'email', 'api', 'manual'].map((type) => (
              <button key={type} className="card card--interactive" style={{ textAlign: 'start' }}
                      onClick={() => { addSource(type); setSheet(null); }}>
                <span className="small strong">{type}</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}
      {sheet === 'delete-data' && (
        <ConfirmSheet
          title={t('deleteData')} body={t('deleteDataHelp')} phrase="DELETE MY DATA"
          onClose={() => setSheet(null)}
          onConfirm={async () => {
            await api.post('/me/delete-data', { confirm: 'DELETE MY DATA' });
            await refreshUser();
            toast(t('done'));
          }}
        />
      )}
      {sheet === 'delete-account' && (
        <ConfirmSheet
          title={t('deleteAccount')} body={t('deleteAccountHelp')} phrase="DELETE MY ACCOUNT"
          onClose={() => setSheet(null)}
          onConfirm={async () => {
            await api.del('/me', { confirm: 'DELETE MY ACCOUNT' });
            onSignedOut();
          }}
        />
      )}
      {newKey && (
        <Sheet title={t('ingestKey')} onClose={() => setNewKey(null)}>
          {/* Told what to do with it in terms of the phone they set up, rather
              than naming the Android app to every user regardless. */}
          <CopyableSecret
            value={newKey.key} label={newKey.type}
            hint={user?.devicePlatform === 'android' ? t('ingestKeyHelpAndroid') : t('ingestKeyHelpIos')}
          />
          <button className="btn btn--primary btn--block" style={{ marginTop: 16 }} onClick={() => setNewKey(null)}>
            {t('done')}
          </button>
        </Sheet>
      )}
    </div>
  );
}

/**
 * Confirm the email address, so a forgotten password is recoverable.
 *
 * Shown only while unconfirmed, and never as an obstacle — the app works
 * either way. What it is really warning about is that without a confirmed
 * address there is no way back into the account, and the transactions in it
 * would be gone for good.
 */
function VerifyEmailCard(): JSX.Element | null {
  const { locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const { data, reload } = useFetch<{ verified: boolean; emailConfigured: boolean }>('/auth/verification-status');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!data || data.verified) return null;

  const send = async () => {
    setBusy(true);
    try {
      await api.post('/auth/send-verification');
      setSent(true);
      toast(ar ? 'أُرسل الرابط' : 'Link sent — check your inbox');
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginTop: 16, borderColor: 'var(--warning)' }}>
      <div className="small strong">
        {ar ? 'أكّد بريدك الإلكتروني' : 'Confirm your email address'}
      </div>
      <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
        {!data.emailConfigured
          ? (ar
              ? 'إرسال البريد غير مُفعّل على هذا الخادم بعد، فلا يمكن التأكيد الآن.'
              : 'Email sending is not configured on this server yet, so this cannot be confirmed right now.')
          : (ar
              ? 'بدون بريد مؤكَّد، لا توجد طريقة لاستعادة حسابك إن نسيت كلمة المرور — وستُفقد حركاتك.'
              : 'Without a confirmed address there is no way back into your account if you forget your password, and your transactions would be lost with it.')}
      </div>
      {data.emailConfigured && (
        <button className="btn btn--secondary btn--sm" style={{ marginTop: 10 }} disabled={busy} onClick={send}>
          {busy ? '…' : sent
            ? (ar ? 'إرسال مرة أخرى' : 'Send it again')
            : (ar ? 'أرسل رابط التأكيد' : 'Send me the link')}
        </button>
      )}
    </Card>
  );
}

/**
 * When you get paid.
 *
 * The single most consequential setting in the app, and until now the only one
 * with no control at all: `PUT /pay-cycle` existed and nothing ever called it,
 * while the Plan screen told people they could change this in Settings. So a
 * user whose cycle was wrong — which, before detection was relaxed, meant every
 * user for their first three months — had no way to say so.
 *
 * It decides what "this month" means. Salaries here land between the 25th and
 * the 28th, so with a calendar month the money that funds August arrives in
 * July and August opens with no income: safe-to-spend has nothing to measure
 * against and reports the whole month as overspend.
 */
function PayCycleCard(): JSX.Element {
  const { locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const { data, reload } = useFetch<{
    anchorDay: number | null; anchored: boolean; snapped: boolean;
    source: 'user' | 'detected' | null; from: string; to: string;
  }>('/pay-cycle');
  const [busy, setBusy] = useState(false);

  const save = async (day: number | null) => {
    setBusy(true);
    try {
      await api.put('/pay-cycle', { day });
      toast(ar ? 'حُفظت دورة الراتب' : 'Pay cycle saved');
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const day = data?.anchorDay ?? null;
  // The days salaries actually land on here, plus "calendar month" for anyone
  // paid irregularly. A 1–31 picker would be honest and unusable.
  const common = [25, 26, 27, 28, 1];

  return (
    <>
      <SectionTitle>{ar ? 'دورة الراتب' : 'Pay cycle'}</SectionTitle>
      <Card>
        <div className="tiny muted" style={{ lineHeight: 1.6, marginBottom: 10 }}>
          {ar
            ? 'راتبك يصل قبل نهاية الشهر ويجب أن يكفيك حتى الراتب التالي، لذا تُحسب الدورة من راتب إلى راتب لا من أول الشهر.'
            : 'Your salary lands before the month ends and has to last until the next one, so the cycle is measured payday to payday rather than from the 1st.'}
        </div>

        {/* One row. Six options wrapping onto two lines made the last one read
            as a different kind of thing from the five above it, when it is the
            sixth answer to the same question. "Calendar" rather than "Calendar
            month" is what makes them fit. */}
        <div className="chiprow">
          {common.map((d) => (
            <button
              key={d}
              className={`chip chip--flex${day === d ? ' chip--active' : ''}`}
              disabled={busy}
              onClick={() => void save(d)}
            >
              {ar ? d : `${d}${ordinalSuffix(d)}`}
            </button>
          ))}
          <button
            className={`chip chip--flex${day === null ? ' chip--active' : ''}`}
            disabled={busy}
            onClick={() => void save(null)}
          >
            {ar ? 'الشهر' : 'Calendar'}
          </button>
        </div>

        {/* Both choices explained in two sentences, whichever is selected —
            this is the moment someone decides between them, and explaining
            calendar months only to people already on calendar months explains
            it to the one person who no longer needs to know. */}
        <div className="tiny muted" style={{ marginTop: 10, lineHeight: 1.65 }}>
          {ar
            ? 'لا يلزم أن يكون اليوم مضبوطًا — الراتب الذي يصل خلال خمسة أيام قبله أو بعده تتبعه الدورة. وإن لم يكن لديك يوم راتب ثابت، اختر «الشهر».'
            : 'The day need not be exact — a salary landing within five days either side moves the cycle with it. No fixed payday? Choose Calendar.'}
        </div>

        {/* Says which of the three states this is, because "the 27th" means
            something different when we guessed it than when you chose it. */}
        <div className="tiny" style={{ marginTop: 12, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
          {!data ? '—' : !data.anchored
            ? (ar
                ? 'الدورة تتبع الشهر الميلادي حاليًا.'
                : 'Currently following calendar months.')
            : data.source === 'user'
              ? (ar
                  ? 'اخترت هذا اليوم بنفسك، ولن يُغيَّر تلقائيًا.'
                  : 'You chose this day, so it will not be changed automatically.')
              : (ar
                  ? 'اكتُشف هذا اليوم من رواتبك، وسيتحسّن كلما وصل راتب جديد.'
                  : 'This day was detected from your salary credits, and improves as more arrive.')}
          {data?.snapped && ` ${ar
            ? `وصل راتبك في يوم مختلف هذه المرة، فبدأت هذه الدورة في ${formatDate(data.from, locale)}.`
            : `Your salary landed on a different day this time, so this cycle began on ${formatDate(data.from, locale)}.`}`}
        </div>
      </Card>
    </>
  );
}

function ordinalSuffix(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th';
}

/**
 * Face ID sign-in.
 *
 * Offered as an addition to the password, never a replacement. A passkey that
 * is not synced to iCloud lives on exactly one device, and someone who has been
 * told they can forget their password will be locked out the day they lose
 * their phone — so the copy says which kind they have.
 */
function PasskeySection(): JSX.Element {
  const { t, locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [items, setItems] = useState<passkeys.PasskeySummary[] | null>(null);
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    passkeys.list().then((list) => {
      setItems(list);
      // The app-lock switch is gated on having a passkey and cannot see this
      // list. Without the broadcast, enrolling Face ID and immediately turning
      // on the lock is refused with "set up Face ID first".
      window.dispatchEvent(new CustomEvent('fintrack:passkeys', { detail: list.length }));
    }).catch(() => setItems([]));
  };

  useEffect(() => {
    passkeys.platformAuthenticatorAvailable().then(setAvailable);
    load();
  }, []);

  const add = async () => {
    setBusy(true);
    try {
      const { deviceName } = await passkeys.register();
      toast(ar ? `تمت إضافة ${deviceName}` : `${deviceName} added`);
      load();
    } catch (err) {
      if (!passkeys.wasCancelled(err)) toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: passkeys.PasskeySummary) => {
    const label = p.deviceName ?? 'this device';
    if (!window.confirm(ar ? `إزالة ${label}؟` : `Remove ${label}?`)) return;
    try {
      await passkeys.remove(p.id);
      toast(t('done'));
      load();
    } catch (err) {
      toastError(err);
    }
  };

  if (!available && !items?.length) {
    return (
      <div style={{ paddingBlock: 4 }}>
        <div className="small strong">{t('faceIdSignIn')}</div>
        <div className="tiny muted" style={{ marginTop: 3 }}>
          {ar ? 'هذا الجهاز لا يدعم القياسات الحيوية.' : 'This device has no biometric sign-in.'}
        </div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div>
        <div className="small strong">{t('faceIdSignIn')}</div>
        <div className="tiny muted" style={{ marginTop: 3, lineHeight: 1.5 }}>
          {ar
            ? 'سجّل الدخول بـ Face ID بدل كتابة كلمة المرور. تبقى كلمة المرور تعمل.'
            : 'Sign in with Face ID instead of typing your password. Your password keeps working.'}
        </div>
      </div>

      {items?.map((p) => (
        <div className="row row--between" key={p.id} style={{ gap: 10 }}>
          <span className="grow">
            <span className="small" style={{ display: 'block' }}>{p.deviceName ?? 'Passkey'}</span>
            <span className="tiny muted">
              {p.backedUp
                ? (ar ? 'محفوظ في iCloud' : 'Saved in iCloud Keychain')
                : (ar ? 'على هذا الجهاز فقط' : 'On this device only')}
              {p.lastUsedAt && ` · ${new Date(p.lastUsedAt).toLocaleDateString()}`}
            </span>
          </span>
          <button className="icon-btn icon-btn--danger" aria-label={`${t('delete')} ${p.deviceName ?? 'passkey'}`}
                  onClick={() => remove(p)}>✕</button>
        </div>
      ))}

      {available && (
        <button className="btn btn--secondary btn--sm" onClick={add} disabled={busy}>
          {busy ? '…' : (items?.length ? t('addAnotherDevice') : t('setUpFaceId'))}
        </button>
      )}
    </div>
  );
}

/**
 * Hide the app when it goes to the background, and ask again on return.
 *
 * Depends on having a passkey: without one there is nothing to unlock with
 * short of signing out entirely, which would be a worse experience than no lock
 * at all. So the switch explains that rather than offering a trap.
 */
function AppLockToggle(): JSX.Element {
  const { locale, toast } = useApp();
  const ar = locale === 'ar';
  const [enabled, setEnabled] = useState(() => readLockPreference().enabled);
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);
  // In the native app the lock opens with a local Face ID check, so a passkey
  // is beside the point. Requiring one there refused to switch on a lock that
  // would have worked perfectly — the web needs a passkey because WebAuthn is
  // the only biometric a browser has, and that reason does not travel.
  const [nativeBiometry, setNativeBiometry] = useState(false);

  useEffect(() => {
    passkeys.list().then((p) => setHasPasskey(p.length > 0)).catch(() => setHasPasskey(false));
    void native.biometryStatus().then((b) => setNativeBiometry(b.available));
    const onChange = (e: Event) => setHasPasskey(((e as CustomEvent<number>).detail ?? 0) > 0);
    window.addEventListener('fintrack:passkeys', onChange);
    return () => window.removeEventListener('fintrack:passkeys', onChange);
  }, []);

  const change = (value: boolean) => {
    if (value && !hasPasskey && !nativeBiometry) {
      toast(ar ? 'أضف Face ID أولاً' : 'Set up Face ID first', 'error');
      return;
    }
    writeLockPreference({ enabled: value });
    setEnabled(value);
    // AppLock is mounted above this screen and cannot see the write.
    window.dispatchEvent(new Event('fintrack:lockpref'));
  };

  return (
    <Toggle
      checked={enabled}
      onChange={change}
      label={ar ? 'قفل التطبيق' : 'Require Face ID to open'}
      hint={ar
        ? 'يطلب Face ID عند فتح فينتراك، ويخفي أرصدتك في شاشة تبديل التطبيقات.'
        : 'Asks for Face ID every time you open Fintrack, and blurs your balances in the app switcher.'}
    />
  );
}

/**
 * Push notifications.
 *
 * The states matter more than the switch. On iOS push is unavailable until the
 * app has been added to the Home Screen, and a toggle that silently fails there
 * is worse than no toggle — so each reason gets its own sentence and its own
 * next step.
 */
function PushCard(): JSX.Element {
  const { t, locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [state, setState] = useState<push.PushState | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => { push.status().then((s) => setState(s.state)).catch(() => setState('unsupported')); };
  useEffect(load, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (state === 'on') { await push.disable(); setState('off'); }
      else setState(await push.enable());
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const delivered = await push.sendTest();
      toast(delivered
        ? (ar ? 'تم الإرسال' : 'Sent — check your lock screen')
        : (ar ? 'لا توجد أجهزة مشتركة' : 'No devices subscribed'), delivered ? 'info' : 'error');
    } finally {
      setBusy(false);
    }
  };

  const explanation: Record<push.PushState, string> = {
    on: ar ? 'ستصلك التنبيهات المهمة على شاشة القفل.' : 'Important alerts will reach your lock screen.',
    off: ar ? 'التنبيهات تظهر داخل التطبيق فقط.' : 'Alerts only appear inside the app right now.',
    denied: ar
      ? 'الإشعارات مرفوضة. فعّلها من إعدادات iPhone ← فينتراك ← الإشعارات.'
      : 'Notifications are blocked. Turn them on in iPhone Settings → Fintrack → Notifications.',
    'needs-install': ar
      ? 'أضف فينتراك إلى الشاشة الرئيسية أولاً — لا يسمح iOS بالإشعارات من متصفح سفاري.'
      : 'Add Fintrack to your Home Screen first — iOS gives no notifications to a Safari tab.',
    'not-configured': ar
      ? 'لم يُفعّل الخادم الإشعارات بعد.'
      : 'This server has no push keys configured yet.',
    unsupported: ar ? 'هذا المتصفح لا يدعم الإشعارات.' : 'This browser cannot show notifications.',
  };

  const canToggle = state === 'on' || state === 'off';

  return (
    <Card>
      <div className="row row--between" style={{ gap: 12 }}>
        <span className="grow">
          <span className="small strong" style={{ display: 'block' }}>{t('pushNotifications')}</span>
          <span className="tiny muted" style={{ lineHeight: 1.5 }}>
            {state ? explanation[state] : '—'}
          </span>
        </span>
        {canToggle && (
          <button className="btn btn--sm btn--secondary" onClick={toggle} disabled={busy}>
            {state === 'on' ? t('turnOff') : t('turnOn')}
          </button>
        )}
      </div>
      {state === 'on' && (
        <>
          <div className="divider" />
          <button className="btn btn--ghost btn--block btn--sm" style={{ justifyContent: 'flex-start' }}
                  onClick={test} disabled={busy}>
            {t('sendTestNotification')}
          </button>
        </>
      )}
    </Card>
  );
}

/**
 * Create an account by hand.
 *
 * Accounts otherwise only appear when a message carries a card's last four
 * digits, so a current account whose bank never sends them could never exist —
 * and therefore could never be the thing a source files into.
 */
function AddAccountSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }): JSX.Element {
  const { t, locale, user, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [displayName, setDisplayName] = useState('');
  const [institution, setInstitution] = useState('');
  const [accountType, setAccountType] = useState('current');
  const [last4, setLast4] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/accounts', {
        displayName: displayName.trim(),
        institution: institution.trim() || null,
        accountType,
        currency: user?.currency,
        last4: last4.trim() ? last4.trim() : null,
      });
      toast(t('done'));
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={ar ? 'إضافة حساب' : 'Add an account'} onClose={onClose}>
      <div className="stack">
        <Field label={ar ? 'الاسم' : 'Name'}>
          <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                 placeholder={ar ? 'الحساب الجاري' : 'Current account'} />
        </Field>
        <Field label={ar ? 'البنك' : 'Bank'}>
          <input className="input" value={institution} onChange={(e) => setInstitution(e.target.value)}
                 placeholder="ADCB" />
        </Field>
        <Field label={t('accountType')}>
          <select className="input" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
            {ACCOUNT_TYPES.map((a) => (
              <option key={a.key} value={a.key}>{ar ? a.ar : a.en}</option>
            ))}
          </select>
        </Field>
        <Field
          label={ar ? 'آخر ٤ أرقام (اختياري)' : 'Last 4 digits (optional)'}
          hint={ar
            ? 'إن أرسلها بنكك في الرسائل، ستُوجَّه الحركات هنا تلقائياً.'
            : 'If your bank includes them in its messages, transactions will find this account on their own.'}
        >
          <input className="input" value={last4} inputMode="numeric" maxLength={4}
                 onChange={(e) => setLast4(e.target.value.replace(/\D/g, ''))} placeholder="1871" />
        </Field>
        <button className="btn btn--primary btn--block" disabled={busy || !displayName.trim()} onClick={save}>
          {busy ? '…' : t('save')}
        </button>
      </div>
    </Sheet>
  );
}

/** Point a source at the account its messages belong to. */
function LinkSourceSheet({ source, accounts, onClose, onSaved }: {
  source: Source;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t, locale, toast, toastError } = useApp();
  const ar = locale === 'ar';
  const [busy, setBusy] = useState(false);

  const choose = async (accountId: string | null) => {
    setBusy(true);
    try {
      await api.patch(`/sources/${source.id}`, { defaultAccountId: accountId });
      toast(t('done'));
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={source.label ?? source.type} onClose={onClose}>
      <div className="stack">
        {/* Lead with what already happens. Someone with several cards on one
            bank feed is here because they assumed each card needed its own key,
            and the honest answer is that it does not. */}
        {(source.routed_accounts?.length ?? 0) > 0 && (
          <Card style={{ background: 'var(--accent-soft)', borderColor: 'transparent' }}>
            <div className="tiny strong" style={{ color: 'var(--accent-text)' }}>
              {ar ? 'هذا المصدر يوزّع تلقائياً' : 'This source already sorts itself'}
            </div>
            <div className="tiny" style={{ color: 'var(--accent-text)', marginTop: 4, lineHeight: 1.55 }}>
              {ar
                ? `تذهب الحركات إلى ${source.routed_accounts.map((a) => a.last4 ? `•${a.last4}` : a.displayName).join('، ')} حسب رقم البطاقة في كل رسالة. مفتاح واحد يكفي لكل هذه البطاقات.`
                : `Transactions go to ${source.routed_accounts.map((a) => a.last4 ? `•${a.last4}` : a.displayName).join(', ')}, decided by the card number in each message. One key covers all of them.`}
            </div>
          </Card>
        )}

        <p className="tiny muted" style={{ lineHeight: 1.6 }}>
          {ar
            ? 'الإعداد أدناه للرسائل التي لا تذكر رقم بطاقة إطلاقاً — مثل حساب جاري أو محفظة. إن ذكرت الرسالة بطاقة، فالبطاقة أولى دائماً.'
            : 'The setting below is only for messages that name no card at all — a current account, or a wallet. When a message does name a card, the card always wins.'}
        </p>

        <button className="btn btn--secondary btn--block" disabled={busy} onClick={() => choose(null)}>
          {ar ? 'بدون حساب محدد' : 'No specific account'}
        </button>

        {accounts.map((a) => (
          <button
            key={a.id}
            className={`btn btn--block ${source.default_account_id === a.id ? 'btn--primary' : 'btn--secondary'}`}
            disabled={busy}
            onClick={() => choose(a.id)}
          >
            {/* Auto-created accounts are already named "Card •1871", so
                appending the digits again reads as "Card •1871 ·1871". */}
            {a.displayName}{a.last4 && !a.displayName.includes(a.last4) ? ` ·${a.last4}` : ''}
          </button>
        ))}

        {!accounts.length && (
          <p className="small muted">
            {ar ? 'أضف حساباً أولاً من قسم الحسابات.' : 'Add an account first, under Accounts & cards.'}
          </p>
        )}
      </div>
    </Sheet>
  );
}

/**
 * The switch that uncovers your own figures.
 *
 * Hidden is the resting state, and it returns to it a minute after every
 * reveal and on every launch. That is the opposite of how a privacy switch is
 * usually built — normally you turn privacy *on* before handing the phone over
 * — and it is the only arrangement that helps, because the moment somebody
 * leans in to look at your screen is precisely the moment you are not thinking
 * about a setting in a menu.
 *
 * Turning it on costs a Face ID check: a switch anyone holding the phone can
 * flip protects nothing from the person most likely to flip it.
 */
function PrivateAmountsCard(): JSX.Element {
  const { locale, amountsHidden, setPrivacyMode, toast } = useApp();
  const ar = locale === 'ar';
  const [busy, setBusy] = useState(false);

  const onToggle = async (on: boolean) => {
    // Turning it on is instant and asks nothing, so there is no waiting state
    // to show — a "Verifying…" that flashes for one frame reads as a stutter.
    if (on) { void setPrivacyMode(true); return; }

    setBusy(true);
    try {
      const ok = await setPrivacyMode(false);
      if (!ok) toast(ar ? 'تعذّر التحقق' : 'Could not verify it is you', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginTop: 10 }}>
      <Toggle
        /* On means covered — and it stays where it is put.

           It began as the opposite: on at every launch, off for three minutes
           at a time, back on by a timer. That guards against forgetting, and
           the cost was that it guarded against its owner too — the app opened
           covered every single time, to protect them during the rare minute
           they were showing somebody else. This is a switch you throw before
           sharing a screen and throw back afterwards, which is the only moment
           it was ever for. */
        checked={amountsHidden}
        /* `next` is the next value of `checked`, which is `amountsHidden`,
           which is privacy mode being on — so it goes straight through. The
           negation that used to sit here was left over from when the switch
           meant the opposite, and it made the control dead in one direction:
           turning the mode on asked for Face ID and then turned it off. */
        onChange={(next) => { if (!busy) void onToggle(next); }}
        label={ar ? 'وضع الخصوصية' : 'Privacy mode'}
        hint={busy
          ? (ar ? 'جارٍ التحقق…' : 'Verifying…')
          : amountsHidden
            /* Names what is covered and what is not, in that order. Covering
               everything was tried and left the owner of the phone reading a
               screen of bullets, so the line is drawn at what you have rather
               than what you spend — and the hint has to say which, or a
               visible figure reads as the switch having failed. */
            ? (ar
                ? 'دخلك والمتاح للإنفاق وأهداف الادخار مخفية، وتبقى كذلك حتى تُطفئ الوضع. إنفاقك وميزانياتك ظاهرة. إطفاؤه يتطلب التحقق منك.'
                : 'Your income, safe to spend and savings goals are covered, and stay covered until you turn this off. Your spending and budgets stay visible. Turning it off asks for Face ID.')
            /* Says what it is for. A switch nobody can see the point of is one
               nobody reaches for at the moment it would have helped. */
            : (ar
                ? 'يغطي دخلك والمتاح للإنفاق وأهداف الادخار. شغّله قبل أن تشارك شاشتك أو تُعطي هاتفك لأحد.'
                : 'Covers your income, safe to spend and savings goals. Turn it on before you share your screen or hand your phone over.')}
      />
    </Card>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <div className="row row--between" style={{ paddingBlock: 7, gap: 16 }}>
      <span className="small muted">{label}</span>
      {/* The hint sits under the value rather than beside it: where a figure
          came from is a footnote to the figure, and a phone has no room for
          both on one line. */}
      <span style={{ textAlign: 'end', minWidth: 0 }}>
        <span className="small truncate" style={{ display: 'block' }}>{value}</span>
        {hint && <span className="tiny muted" style={{ display: 'block' }}>{hint}</span>}
      </span>
    </div>
  );
}

/**
 * Countries offered for the profile, mirroring onboarding.
 *
 * Each carries its usual currency so picking a country fills the currency in,
 * but the two stay independent afterwards — living in one country and keeping
 * accounts in another currency is the normal case here, not an edge case.
 */
const COUNTRIES: { code: string; en: string; ar: string; currency: string }[] = [
  { code: 'AE', en: 'United Arab Emirates', ar: 'الإمارات العربية المتحدة', currency: 'AED' },
  { code: 'SA', en: 'Saudi Arabia', ar: 'السعودية', currency: 'SAR' },
  { code: 'QA', en: 'Qatar', ar: 'قطر', currency: 'QAR' },
  { code: 'KW', en: 'Kuwait', ar: 'الكويت', currency: 'KWD' },
  { code: 'BH', en: 'Bahrain', ar: 'البحرين', currency: 'BHD' },
  { code: 'OM', en: 'Oman', ar: 'عُمان', currency: 'OMR' },
  { code: 'GB', en: 'United Kingdom', ar: 'المملكة المتحدة', currency: 'GBP' },
  { code: 'US', en: 'United States', ar: 'الولايات المتحدة', currency: 'USD' },
  { code: 'IN', en: 'India', ar: 'الهند', currency: 'INR' },
  { code: 'EG', en: 'Egypt', ar: 'مصر', currency: 'EGP' },
];

const CURRENCIES = ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'USD', 'EUR', 'GBP', 'INR', 'EGP'];

function EditProfileSheet({ onClose }: { onClose: () => void }): JSX.Element {
  const { t, locale, user, refreshUser, toast } = useApp();
  const [name, setName] = useState(user?.name ?? '');
  const [country, setCountry] = useState(user?.country ?? 'AE');
  const [currency, setCurrency] = useState(user?.currency ?? 'AED');
  // Prefilled from the last salary when nothing is stated, so confirming what
  // the app already worked out is a tap rather than typing a figure back in.
  const [income, setIncome] = useState(() => {
    const minor = user?.monthlyIncomeMinor ?? user?.detectedIncome?.amountMinor ?? null;
    return minor != null && user ? String(minorToDecimal(minor, user.currency)) : '';
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const trimmed = income.trim();
    const incomeMinor = trimmed ? parseAmountInput(trimmed, currency) : null;
    if (trimmed && incomeMinor == null) {
      setError(locale === 'ar' ? 'مبلغ غير صالح' : 'That amount could not be read');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.patch('/me', {
        // An empty name clears it rather than saving "", which would render as
        // a blank row instead of the "—" placeholder.
        name: name.trim() || null,
        country,
        currency,
        monthlyIncomeMinor: incomeMinor,
      });
      await refreshUser();
      toast(t('done'));
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('editProfile')} onClose={onClose}>
      <div className="stack">
        <Field label={t('email')} hint={t('emailLocked')}>
          <input className="input" value={user?.email ?? ''} disabled />
        </Field>

        <Field label={t('name')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>

        <Field label={t('country')}>
          <select
            className="input"
            value={country}
            onChange={(e) => {
              const next = e.target.value;
              setCountry(next);
              // Follow the country's currency only while the user hasn't
              // deliberately chosen a different one.
              const match = COUNTRIES.find((c) => c.code === next);
              if (match && currency === COUNTRIES.find((c) => c.code === country)?.currency) {
                setCurrency(match.currency);
              }
            }}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{locale === 'ar' ? c.ar : c.en}</option>
            ))}
          </select>
        </Field>

        <Field label={t('currency')} hint={t('currencyChangeHelp')}>
          <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label={`${t('monthlyIncome')} (${currency})`} error={error}>
          <input
            className="input" inputMode="decimal" value={income}
            onChange={(e) => setIncome(e.target.value)} placeholder="—"
          />
        </Field>

        <button className="btn btn--primary btn--block" disabled={busy} onClick={save}>
          {busy ? <Spinner /> : t('save')}
        </button>
      </div>
    </Sheet>
  );
}

const ACCOUNT_TYPES: { key: string; en: string; ar: string }[] = [
  { key: 'current', en: 'Current account', ar: 'حساب جاري' },
  { key: 'savings', en: 'Savings account', ar: 'حساب توفير' },
  { key: 'card', en: 'Debit card', ar: 'بطاقة خصم' },
  { key: 'credit_card', en: 'Credit card', ar: 'بطاقة ائتمان' },
  { key: 'wallet', en: 'Wallet', ar: 'محفظة' },
  { key: 'cash', en: 'Cash', ar: 'نقد' },
];

function EditAccountSheet({ account, onClose, onSaved }: {
  account: Account;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t, locale, toast } = useApp();
  const [displayName, setDisplayName] = useState(account.displayName);
  const [institution, setInstitution] = useState(account.institution ?? '');
  const [accountType, setAccountType] = useState(account.accountType);
  const [balance, setBalance] = useState(
    account.balanceMinor != null ? String(minorToDecimal(account.balanceMinor, account.currency)) : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!displayName.trim()) {
      setError(locale === 'ar' ? 'الاسم مطلوب' : 'A name is required');
      return;
    }
    const trimmed = balance.trim();
    const balanceMinor = trimmed ? parseAmountInput(trimmed, account.currency) : null;
    if (trimmed && balanceMinor == null) {
      setError(locale === 'ar' ? 'رصيد غير صالح' : 'That balance could not be read');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.patch(`/accounts/${account.id}`, {
        displayName: displayName.trim(),
        institution: institution.trim() || null,
        accountType,
        balanceMinor,
      });
      onSaved();
      toast(t('done'));
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('editAccount')} onClose={onClose}>
      <div className="stack">
        <Field label={t('name')}>
          <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoFocus />
        </Field>

        <Field label={t('institution')}>
          <input className="input" value={institution} onChange={(e) => setInstitution(e.target.value)} placeholder="—" />
        </Field>

        <Field label={t('accountType')}>
          <select className="input" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
            {ACCOUNT_TYPES.map((a) => (
              <option key={a.key} value={a.key}>{locale === 'ar' ? a.ar : a.en}</option>
            ))}
          </select>
        </Field>

        {/* Balances normally arrive from bank messages. Editing one by hand is
            for the account your bank never messages about — so it is offered,
            and stamped as of now like any other balance update. */}
        <Field label={`${t('accountBalance')} (${account.currency})`} error={error}>
          <input
            className="input" inputMode="decimal" value={balance}
            onChange={(e) => setBalance(e.target.value)} placeholder="—"
          />
        </Field>

        <button className="btn btn--primary btn--block" disabled={busy} onClick={save}>
          {busy ? <Spinner /> : t('save')}
        </button>
      </div>
    </Sheet>
  );
}

function ChangePasswordSheet({ onClose }: { onClose: () => void }): JSX.Element {
  const { t, toast } = useApp();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      toast(t('done'));
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('changePassword')} onClose={onClose}>
      <div className="stack">
        <Field label={t('currentPassword')}>
          <input className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                 autoComplete="current-password" />
        </Field>
        <Field label={t('newPassword')} hint={t('passwordHint')} error={error}>
          <input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)}
                 autoComplete="new-password" minLength={10} />
        </Field>
        <button className="btn btn--primary btn--block" disabled={busy} onClick={save}>{t('save')}</button>
      </div>
    </Sheet>
  );
}

function AlertSettingsSheet({ onClose }: { onClose: () => void }): JSX.Element {
  const { t, locale, toastError } = useApp();
  const { data, reload } = useFetch<{ alerts: Record<string, boolean>; alertTypes: string[] }>('/preferences');
  const [saving, setSaving] = useState(false);

  const setAlert = async (key: string, value: boolean) => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put('/preferences/alerts', { value: { ...data.alerts, [key]: value } });
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title={t('alertSettings')} onClose={onClose}>
      <div className="stack stack--tight">
        {data?.alertTypes.map((key) => (
          <Toggle
            key={key}
            checked={data.alerts[key] !== false}
            onChange={(v) => !saving && setAlert(key, v)}
            label={ALERT_LABELS[key]?.[locale] ?? key}
          />
        ))}
      </div>
    </Sheet>
  );
}

function ConfirmSheet({ title, body, phrase, onClose, onConfirm }: {
  title: string; body: string; phrase: string;
  onClose: () => void; onConfirm: () => Promise<void>;
}): JSX.Element {
  const { t, toastError } = useApp();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={title} onClose={onClose}>
      <div className="stack">
        <p className="small muted" style={{ lineHeight: 1.6 }}>{body}</p>
        <Field label={`${t('typeConfirm')} "${phrase}" ${t('toConfirm')}`}>
          <input ref={inputRef} className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        </Field>
        <div className="row" style={{ gap: 10 }}>
          <button className="btn btn--secondary grow" onClick={onClose}>{t('cancel')}</button>
          <button className="btn btn--danger grow" disabled={busy || typed !== phrase} onClick={run}>
            {t('delete')}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
